import React, { useState } from 'react';
import {
    Form,
    Space,
    Modal,
    Upload,
    Button,
    Table,
    message,
    Typography,
} from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { apiRequest } from '../../../../service/apiRequest';

const { Text } = Typography;

interface Props {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

// Use the full Excel key list you shared
const ledgerKeys = [
    "id", "accountopenon", "acctgroup", "addedupdatedon", "address", "addressline1", "addressline2",
    "bank_acc", "bank_acc_name", "bankaddress", "bankcity", "bankemail", "bankfax", "bankname",
    "bankphone", "birthanni", "ccid", "child_account_group_list", "city", "cmtitle", "companyname",
    "contact_person_name", "country", "credit_limit", "credit_period", "discountpercentage", "division",
    "ecode_preparedby", "ecode_salesperson", "email", "email2", "exporter", "fileno", "finyear",
    "gstno", "iecno", "ifsc_code", "industrytype", "landline", "manufacturer", "marryanni", "op_type",
    "opening_bal", "pan", "pan_no", "parent_account_group_list", "payment_mode", "phone1", "phone2",
    "pincode", "poboxno", "portno", "remarks", "retailer", "shippingaddress", "sourcing", "statecode",
    "stateloc", "stdcode", "swiftcode", "trader", "verifiedby1", "visible_status", "website", "workanni",
    "accounthead", "anniversary", "birthday", "catalogue_status", "deals_in", "deals_in_company",
    "designation", "faxno", "instagram", "landmark", "notes", "pannumber", "skype", "states", "tinno",
    "youtube", "companyaddress", "companyphone", "firstname", "lastname", "bank_entry", "bill",
    "city_office", "cmp_name", "credit_limit_amt", "interest", "interest_rate", "interest_style",
    "mobile_id", "msme_register_no", "occupation", "offer_festival", "office_address", "office_country",
    "office_landmark", "office_pincode", "passwrd", "product_deals_in", "social_media", "stateloc_office",
    "tin_no", "groupccid", "groupname", "billtobill", "subaccounthead", "lastupdateddate", "emp_code", "companycode"
];

// Generate a sample template with empty or default values
const sampleTemplate = [
    Object.fromEntries(ledgerKeys.map((key) => [key, '']))
];

const ImportLedgerModal: React.FC<Props> = ({ visible, onClose, onSuccess }) => {
    const [parsedData, setParsedData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const downloadSample = () => {
        const ws = XLSX.utils.json_to_sheet(sampleTemplate);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ledgerTemplate');
        XLSX.writeFile(wb, 'ledger_sample_template.xlsx');
    };

    const handleUpload = async (file: File) => {
        setLoading(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });

            if (!json.length) {
                message.warning('The Excel file is empty.');
                return false;
            }

            setParsedData(json);
        } catch (err) {
            console.error(err);
            message.error('Failed to read Excel file. Please check the format.');
        } finally {
            setLoading(false);
        }

        return false; // prevent auto-upload
    };

    const handleImport = async () => {
        try {
            await apiRequest({
                method: 'post',
                url: '/import-ledgers',
                data: parsedData,
            });
            message.success('Ledger imported successfully!');
            onSuccess();
            onClose();
        } catch (err) {
            console.error(err);
            message.error('Import failed. Check the data format.');
        }
    };

    return (
        <Modal
            title="Import Ledger"
            width="90%"
            open={visible}
            onCancel={onClose}
            onOk={handleImport}
            okText="Import"
            okButtonProps={{ disabled: parsedData.length === 0 || loading, loading }}
            destroyOnClose
        >
            <Form layout="vertical">
                <Form.Item label="Step 1">
                    <Space direction="vertical" size={4}>
                        <Text type="secondary">Download and fill in the sample template</Text>
                        <Button icon={<DownloadOutlined />} onClick={downloadSample}>
                            Download Sample Template
                        </Button>
                    </Space>
                </Form.Item>

                <Form.Item label="Step 2">
                    <Space direction="vertical" size={4}>
                        <Text type="secondary">Upload the filled template</Text>
                        <Upload
                            accept=".xlsx"
                            beforeUpload={handleUpload}
                            showUploadList={false}
                            disabled={loading}
                        >
                            <Button icon={<UploadOutlined />} loading={loading}>
                                Upload Excel File
                            </Button>
                        </Upload>
                    </Space>
                </Form.Item>
            </Form>

            {parsedData.length > 0 && (
                <>
                    <Text style={{ marginTop: 12, display: 'block' }}>Preview of Imported Data:</Text>
                    <Table
                        dataSource={parsedData}
                        rowKey={(_, index) => index!.toString()}
                        size="small"
                        scroll={{ y: 300, x: 'max-content' }}
                        pagination={false}
                        columns={Object.keys(parsedData[0] || {}).map((key) => ({
                            title: key,
                            dataIndex: key,
                            key,
                        }))}
                        style={{ marginTop: 8 }}
                    />
                </>
            )}
        </Modal>
    );
};

export default ImportLedgerModal;
